import React, { FC, useRef, useEffect, useState, useContext } from 'react';
import Editor from '@monaco-editor/react';
import Header from '@/components/DesignLibrary/Header';
import ProgressIndicator from '@/components/ProgressIndicator';
import useNoodleApi from '@/hooks/useNoodleApi';
import * as tsClient from '@/tsClient';
import { ListAndDetailsSubLayoutContext } from '@/layouts/ListAndDetailsSubLayout';
import previewDynamicHtmlTemplatesPdf from '@/tsClient/workflows/previewDynamicHtmlTemplatesPdf';
import Content from '@/components/Document/DocumentPreview/Content';
import s from './EditGovernmentFormContent.module.scss';
import handleBarsTemplateUtils from '../../helpers/handleBarsTemplateUtils';

const BASE_WIDTH = 960;

const EditGovernmentFormContent: FC<{ id: string }> = ({ id }) => {
  const { reloadListView } = useContext(ListAndDetailsSubLayoutContext);

  const [fileName, setFileName] = useState<'index.html' | 'styles.css' | 'scripts.js' | 'sampleData.json'>('index.html');
  const [cssContent, setCssContent] = useState('');
  const [jsContent, setJsContent] = useState('');
  const [jsonContent, setJsonContent] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [compiledHtml, setCompiledHtml] = useState('');
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);

  // API calls for HTML
  const { data: htmlWithJson, fetchingState: htmlFetchingState, getData: getHtmlByIdFn } = useNoodleApi(tsClient.getDynamicPdfTemplateById);
  const { data: dynamicTemplateAssets, fetchingState: dynamicTemplateAssetsFetchingState, getData:
     getDynamicPdfTemplatesAssetsByCategory } = useNoodleApi(tsClient.getDynamicPdfTemplatesAssetsByCategory);

  const cssData = dynamicTemplateAssets?.find(asset => asset.assetType === 'css');
  const jsData = dynamicTemplateAssets?.find(asset => asset.assetType === 'js');
  const jsonData = htmlWithJson?.jsonAsset;

  const [isHtmlMode, setIsHtmlMode] = useState(true);

  useEffect(() => {
    if (htmlWithJson?.content) {
      setHtmlContent(htmlWithJson.content);
    }
    if(htmlWithJson?.jsonAsset) {
      setJsonContent(htmlWithJson?.jsonAsset?.content || '');
    }
  }, [htmlWithJson]);

  useEffect(() => {
    if (cssData?.content) {
      setCssContent(cssData.content);
    }
    if (jsData?.content) {
      setJsContent(jsData.content);
    }
  }, [cssData, jsData]);

  const {
    fetchingState: { isFetching: isHtmlUpdating },
    getData: updateHtmlFn,
  } = useNoodleApi(tsClient.updateDynamicPdfTemplate);

  const {
    fetchingState: { isFetching: isCssUpdating },
    getData: updateCssFn,
  } = useNoodleApi(tsClient.updateDynamicPdfTemplateAsset);

  const {
    fetchingState: { isFetching: isJsUpdating },
    getData: updateJsFn,
  } = useNoodleApi(tsClient.updateDynamicPdfTemplateAsset);

  const {
    fetchingState: { isFetching: isJsonUpdating },
    getData: updateJsonFn,
  } = useNoodleApi(tsClient.updateDynamicPdfTemplateAsset);

  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (id) {
      getHtmlByIdFn({ id });
      getDynamicPdfTemplatesAssetsByCategory({ category: 'bankruptcy' });
    }
  }, [id]);

  // Update compiled HTML when dependencies change
  useEffect(() => {
    if (htmlContent && jsonContent) {
      try {
        const compiled = handleBarsTemplateUtils.getCombinedTemplateContent({
          compile: true,
          css: cssContent,
          html: htmlContent,
          js: jsContent
            ? {
              content: jsContent,
              slug: 'global-js', // @todo to implement proper logic instead of slug for security purposes
            }
            : undefined,
          json: jsonContent,
        });
        setCompiledHtml(compiled);
      } catch (error) {
        console.error('Error updating template:', error);
        setCompiledHtml(`<div style="color: red; padding: 20px;">Error compiling template: ${error instanceof Error ? error.message : 'Unknown error'}</div>`);
      }
    }
  }, [htmlContent, cssContent, jsContent, jsonContent]);

  const handleEditorChange = (value: string | undefined): void => {
    if (value === undefined) return;

    switch (fileName) {
    case 'index.html':
      setHtmlContent(value);
      break;
    case 'styles.css':
      setCssContent(value);
      break;
    case 'scripts.js':
      setJsContent(value);
      break;
    case 'sampleData.json':
      setJsonContent(value);
      break;
    default:
      break;
    }
  };

  if (htmlFetchingState.isFetching || dynamicTemplateAssetsFetchingState.isFetching) {
    return (
      <div className={s.padding}>
        <ProgressIndicator isCentered />
      </div>
    );
  }

  if (!htmlWithJson || !cssData) {
    return (
      <div className={s.padding}>
        <p className="body-sm">No template found</p>
      </div>
    );
  }

  const combinedContent = handleBarsTemplateUtils.getCombinedTemplateContent({
    compile: false,
    css: cssContent,
    html: compiledHtml || htmlContent,
    js: jsContent
      ? {
        content: jsContent,
        slug: 'global-js', // @todo to implement proper logic instead of slug for security purposes
      }
      : undefined,
    json: jsonContent,
  });

  return (
    <>
      <Header
        title={htmlWithJson.name}
        tabs={[
          {
            label: 'index.html',
            onClick: () => setFileName('index.html'),
            selected: fileName === 'index.html',
          },
          {
            label: 'styles.css',
            onClick: () => setFileName('styles.css'),
            selected: fileName === 'styles.css',
          },
          {
            label: 'scripts.js',
            onClick: () => setFileName('scripts.js'),
            selected: fileName === 'scripts.js',
          },
          {
            label: 'sample-data.json',
            onClick: () => setFileName('sampleData.json'),
            selected: fileName === 'sampleData.json',
          },
        ]}
        actions={[
          {
            isFetching: isCssUpdating || isHtmlUpdating || isJsUpdating || isJsonUpdating,
            isPrimary: true,
            label: 'Save template files',
            onClick: async () => {
              const htmlUpdates = [
                updateHtmlFn(htmlWithJson.id, { content: htmlContent }),
              ];

              const assetUpdates = [];
              if (cssData?.id) {
                assetUpdates.push(updateCssFn(cssData.id, { content: cssContent, id: cssData.id }));
              }

              if (jsData?.id) {
                assetUpdates.push(updateJsFn(jsData.id, { content: jsContent, id: jsData.id }));
              }

              if (jsonData?.id) {
                assetUpdates.push(updateJsonFn(jsonData.id, { content: jsonContent, id: jsonData.id }));
              }

              await Promise.all([...htmlUpdates, ...assetUpdates]);
              await reloadListView();
            },
          },
          {
            isFetching: isPdfGenerating,
            isPrimary: true,
            label: isHtmlMode ? 'Toggle to PDF' : 'Toggle to HTML',
            onClick: async () => {
              if (isHtmlMode) {
                try {
                  setIsPdfGenerating(true);
                  const json = JSON.parse(jsonContent);
                  const response = await previewDynamicHtmlTemplatesPdf({ id, jsonData: json });

                  if (response.pdfBuffer) {
                    const bufferData = (response.pdfBuffer as unknown as { data: Uint8Array }).data || response.pdfBuffer;
                    const blob = new Blob([new Uint8Array(bufferData)], { type: 'application/pdf' });
                    const url = URL.createObjectURL(blob);
                    setPdfUrl(url);
                  }
                } catch (error) {
                  console.error('Error generating PDF', error);
                  setPdfUrl(null);
                } finally {
                  setIsPdfGenerating(false);
                }
              }
              setIsHtmlMode(!isHtmlMode);
            },
          },
        ]}
      />
      <div className={s.container}>
        <Editor
          height="100%"
          defaultLanguage={
            fileName === 'index.html'
              ? 'html'
              : fileName === 'styles.css'
                ? 'css'
                : fileName === 'scripts.js' ? 'javascript' : 'json'
          }
          theme="light"
          path={fileName}
          value={
            fileName === 'index.html'
              ? htmlContent
              : fileName === 'styles.css'
                ? cssContent
                : fileName === 'scripts.js' ? jsContent : jsonContent
          }
          onChange={handleEditorChange}
          loading={<ProgressIndicator />}
          options={{
            fontSize: 14,
            formatOnPaste: true,
            formatOnType: true,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            wordWrap: 'on',
          }}
        />
        <div className={s.iframe}>
          {isHtmlMode
            ? (
              <iframe
                ref={iframeRef}
                srcDoc={combinedContent}
                title="Preview"
                onLoad={() => {
                  if (iframeRef.current) {
                    const scale = window.innerWidth / 1.66 / BASE_WIDTH;
                    iframeRef.current.style.transform = `scale(${scale})`;
                    iframeRef.current.style.transformOrigin = 'center center';
                  }
                }}
                style={{
                  height: '100%',
                  width: BASE_WIDTH,
                }}
              />
            )
            : (
              isPdfGenerating
                ? (
                  <ProgressIndicator isCentered />
                )
                : (
                  pdfUrl && (
                    <div style={{
                      height: '100%',
                      width: BASE_WIDTH,
                    }}>
                      <Content url={pdfUrl} mimeType="application/pdf" />
                    </div>
                  )
                )
            )}
        </div>
      </div>
    </>
  );
};

export default EditGovernmentFormContent;
